import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/requests/iassignment';
import { IAssignment as IRequestAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import {
  IUsersAssignments,
  IUserWithTeam,
} from 'src/app/interfaces/self-serve/assignment-manager/requests/iusers-assignments';
import { ISimulationByModuleAssignment } from 'src/app/interfaces/self-serve/assignment-manager/responses/isimulation-by-module-assignment';
import { IAddSimulationsToAssignment, IUpdateSimulationsLink } from 'src/app/interfaces/self-serve/assignment-manager/requests/isimulation-in-assignment';
import { IValidateUuidsResponse } from 'src/app/interfaces/self-serve/assignment-manager/responses/ivalidate-uuids-response';
import { IUserAssignmentSummary } from 'src/app/interfaces/self-serve/assignment-manager/responses/iuser-assignment-summary';

export const ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS = 409;

@Injectable({
  providedIn: 'root',
})
export class AssignmentsService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  findAllAssignments(accountUuid: string): Observable<any[]> {
    return this.http
      .get<any[]>(
        `${this.backendUrl}/moduleAssignment/getWithUsersCountByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        tap((res) => console.log('Fetched all assignments:', res)),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/getWithUsersCountByAccountUuid?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  findAssignmentsForSimulation(simulationId: string, moduleUuid: string): Observable<IRequestAssignment[]> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
      
    return this.http
      .get<IRequestAssignment[]>(
        `${this.backendUrl}/moduleAssignment/findByCallAndAccountUuid?callUuid=${simulationId}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        tap((res) =>
          console.log(`Fetched all assignments for simulation ${simulationId} with module uuid ${moduleUuid}`, res)
        ),
        catchError((error) => {
          return this.errorHandlingService.handleError<IRequestAssignment[]>(
            `/moduleAssignment/findByCallAndAccountUuid?callUuid=${simulationId}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }


  createAssignmentAPI(assignmentData: IAssignment): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post<any>(`${this.backendUrl}/moduleAssignment/createWithinAccount`, {
        ...assignmentData,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'failed to create';
          console.log(`assignment Create: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/createWithinAccount`
          )(error);
        })
      );
  }

  duplicateAssignmentAPI(payload: {
    assignmentUuid: string;
    assignmentName?: string;
  }): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const formattedPayload = {
      moduleAssignmentUuid: payload.assignmentUuid,
      moduleAssignmentName: payload.assignmentName,
      accountUuid,
    };

    return this.http
      .post(
        `${this.backendUrl}/moduleAssignment/duplicateStagedAssignment`,
        formattedPayload
      )
      .pipe(
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Assignment duplicated: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/duplicateStagedAssignment`
          )(error);
        })
      );
  }

  updateAssignment(
    moduleAssignmentUuid: string,
    data: IAssignment
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { ...data, accountUuid, moduleAssignmentUuid };

    return this.http
      .post(
        `${this.backendUrl}/moduleAssignment/updateWithinAccount`,
        updatedData
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'updated' : 'failed to update';
          console.log(`update assignment: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/updateWithinAccount`
          )(error);
        })
      );
  }

  getAssignmentDetails(moduleAssignmentUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<any>(
        `${this.backendUrl}/moduleAssignment/getAssignmentDetails?moduleAssignmentUuid=${moduleAssignmentUuid}&&accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Assignment Details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/getAssignmentDetails?moduleAssignmentUuid=${moduleAssignmentUuid}&&accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  getUserTeams(moduleAssignmentUuid: string) {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .get<any>(
        `${this.backendUrl}/moduleAssignment/getUserTeamsByAccountAndModuleAssignmentUuid?moduleAssignmentUuid=${moduleAssignmentUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(
            `Get User Teams By Account And ModuleAssignmentUuid: ${outcome}`
          );
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/getUserTeamsByAccountAndModuleAssignmentUuid?callUuid=${moduleAssignmentUuid}`
          )(error);
        })
      );
  }

  removeAssignment(
    moduleAssignmentUuid: string,
    userUuid: string,
    teamUuid: string
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .delete(
        `${this.backendUrl}/moduleAssignment/removeAssignmentLinkWithUserAndTeam?moduleAssignmentUuid=${moduleAssignmentUuid}&userUuid=${userUuid}&teamUuid=${teamUuid}&accountUuid=${accountUuid}`,
        {
          responseType: 'text',
        }
      )
      .pipe(
        tap(() => console.log(`Assignment removed`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/removeAssignmentLinkWithUserAndTeam`
          )(error);
        })
      );
  }

  deleteStagedAssignmentAPI(moduleAssignmentUuid: string): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const url = `${this.backendUrl}/moduleAssignment/deleteStagedAssignment?uuid=${moduleAssignmentUuid}&accountUuid=${accountUuid}`;
    return this.http.delete(url).pipe(
      tap(() => {
        console.log(`Assignment ${moduleAssignmentUuid} deleted successfully`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/moduleAssignment/deleteStagedAssignment?uuid=${moduleAssignmentUuid}`
        )(error);
      })
    );
  }

  getAllUsersWithTeams() {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .get<any>(
        `${this.backendUrl}/user/findUsersWithTeamsByAccount?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(
            `Get User Teams By Account And ModuleAssignmentUuid: ${outcome}`
          );
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/user/findUsersWithTeamsByAccount?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  assignUsersWithTeamContext(
    moduleAssignmentUuid: string,
    userTeams: IUserWithTeam[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { userTeams, accountUuid, moduleAssignmentUuid };

    return this.http
      .post(
        `${this.backendUrl}/moduleAssignment/assignUsersWithTeamContext`,
        updatedData
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'Added' : 'failed to add';
          console.log(
            `/moduleAssignment/assignUsersWithTeamContext: ${outcome}`
          );
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/assignUsersWithTeamContext`
          )(error);
        })
      );
  }

  addSimulationsToAssignment(payload: IAddSimulationsToAssignment): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<any>(
        `${this.backendUrl}/moduleAssignment/addBulkSimulationLinks`,
        { ...payload, accountUuid }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'failed to create';
          console.log(`Add simulatioms to assignment: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/moduleAssignment/addBulkSimulationLinks`
          )(error);
        })
      );
  }

  getSimulationsByModuleAssignmentUuid(
    moduleAssignmentUuid: string
  ): Observable<ISimulationByModuleAssignment> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const endpoint = `${this.backendUrl}/moduleAssignment/findSimulations?moduleAssignmentUuid=${moduleAssignmentUuid}&accountUuid=${accountUuid}`;

    return this.http.get<ISimulationByModuleAssignment>(endpoint).pipe(
      tap((res) => {
        const outcome = res?.simulations?.length ? 'fetched' : 'did not find';
        console.log(`simulations: ${outcome}`);
      }),
      catchError(
        this.errorHandlingService.handleError<ISimulationByModuleAssignment>(
          endpoint
        )
      )
    );
  }

  updateSimulationsLink(payload: IUpdateSimulationsLink): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(
        `${this.backendUrl}/moduleAssignment/updateSimulationsLink`,
        { ...payload, accountUuid }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'updated' : 'not changed';
          console.log(`Simulations link ${outcome} successfully`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            '/moduleAssignment/updateSimulationsLink'
          )(error);
        })
      );
  }

  removeSimulationFromAssignment(
    moduleAssignmentUuid: string,
    callUuid: string
  ): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const url = `${this.backendUrl}/moduleAssignment/removeSimulationLink?moduleAssignmentUuid=${moduleAssignmentUuid}&callUuid=${callUuid}&accountUuid=${accountUuid}`;
    return this.http.delete(url, { responseType: 'text' }).pipe(
      map((result) => result),
      tap((res) => {
        const outcome = res ? 'deleted' : 'not found';
        console.log(`Simulation ${outcome} from assignment`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/moduleAssignment/removeSimulation?moduleAssignmentUuid=${moduleAssignmentUuid}&callUuid=${callUuid}`
        )(error);
      })
    );
  }

  excludeFromReporting(
    moduleAssignmentUuid: string,
    accountUuid: string,
    userUuid: string,
    excludeReporting: boolean
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.backendUrl}/moduleAssignment/excludeFromReporting`,
        {
          moduleAssignmentUuid,
          accountUuid,
          userUuid,
          excludeReporting,
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'updated' : 'failed to update';
          console.log(`Exclude from reporting: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<boolean>(
            `/moduleAssignment/excludeFromReporting`
          )(error);
        })
      );
  }

  updateAssignmentStatus(moduleAssignmentUuid: string, targetStatus: string): Observable<{ status: string }> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<{ status: string }>(`${this.backendUrl}/moduleAssignment/updateStatus`, { moduleAssignmentUuid, accountUuid, targetStatus })
      .pipe(
        tap((res) => console.log(`Assignment status updated to ${res.status}`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<{ status: string }>('/moduleAssignment/updateStatus')(error);
        })
      );
  }

  validateQuestionUuids(callUuid: string, questionUuids: string[]): Observable<IValidateUuidsResponse> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const uuidsParam = questionUuids.join(',');
    const url = `${this.backendUrl}/call/validateQuestionUuids?callUuid=${callUuid}&questionUuids=${uuidsParam}&accountUuid=${accountUuid}`;
    return this.http.get<IValidateUuidsResponse>(url).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleError<IValidateUuidsResponse>(
          `/call/validateQuestionUuids?callUuid=${callUuid}`
        )(error);
      })
    );
  }

  // ── User Detail Page APIs ──────────────────────────────────────────────────

  getUserAssignmentsInAccount(userUuid: string, accountUuid: string): Observable<IUserAssignmentSummary[]> {
    return this.http
      .get<IUserAssignmentSummary[]>(
        `${this.backendUrl}/user/getUserAssignmentsInAccount?userUuid=${userUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        tap((res) => console.log('Fetched user assignments:', res)),
        catchError((error) =>
          this.errorHandlingService.handleError<any[]>(
            `/user/getUserAssignmentsInAccount`
          )(error)
        )
      );
  }

  removeUserFromAssignment(userUuid: string, moduleAssignmentUuid: string, accountUuid: string): Observable<any> {
    return this.http
      .delete(
        `${this.backendUrl}/user/removeUserFromAssignment?userUuid=${userUuid}&moduleAssignmentUuid=${moduleAssignmentUuid}&accountUuid=${accountUuid}`,
        { responseType: 'text' }
      )
      .pipe(
        tap(() => console.log('User removed from assignment')),
        catchError((error) =>
          this.errorHandlingService.handleError<any>(
            `/user/removeUserFromAssignment`
          )(error)
        )
      );
  }

  getAccountTeams(accountUuid: string): Observable<{ uuid: string; name: string }[]> {
    return this.http
      .get<{ uuid: string; name: string }[]>(
        `${this.backendUrl}/user/getAccountTeams?accountUuid=${accountUuid}`
      )
      .pipe(
        catchError((error) =>
          this.errorHandlingService.handleError<{ uuid: string; name: string }[]>(
            `/user/getAccountTeams?accountUuid=${accountUuid}`
          )(error)
        )
      );
  }

  addUserToAssignment(userUuid: string, moduleAssignmentUuid: string, teamUuids: string[], accountUuid: string): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/user/addUserToAssignment`, {
        userUuid,
        moduleAssignmentUuid,
        accountUuid,
        teamUuids,
      }, { responseType: 'text' as 'json' })
      .pipe(
        tap((res) => console.log('Added user to assignment:', res)),
        catchError((error) =>
          this.errorHandlingService.handleError<any>(
            `/user/addUserToAssignment`
          )(error)
        )
      );
  }

  updateUserTeamAssociations(userUuid: string, moduleAssignmentUuid: string, teamUuids: string[], accountUuid: string): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/user/updateUserTeamAssociations`, {
        userUuid,
        moduleAssignmentUuid,
        accountUuid,
        teamUuids,
      }, { responseType: 'text' as 'json' })
      .pipe(
        tap((res) => console.log('Updated team associations:', res)),
        catchError((error) =>
          this.errorHandlingService.handleError<any>(
            `/user/updateUserTeamAssociations`
          )(error)
        )
      );
  }
}
